import { useEffect, useRef, useState } from 'react';
import { callApiWithAuthorization } from '../../API/CallApi';
import Markdown from 'react-markdown';
import { getEntities } from '../../API/Entity/Entity';
import { getEntityPages } from '../../API/Display/Display';
import { useMsal } from "@azure/msal-react";
import { getProject, updateProjectPropertiesAfterPublish } from '../../API/Project/project';
import { generateIncrementalChanges, removeAllChanges, validateForPublish } from "../../API/Generation/Generation";
import { getInstances, publishApplication, updateProjectPortalConfig } from "../Helpers/Launch";
import { getLoggedInUserUsingAccessToken } from "../../API/User/User";
import { postMessageToRuntime } from "../Helpers/Runtime/runtime";
import { DropDownTree } from "@progress/kendo-react-dropdowns";
import { getProjectRelations } from "../Helpers/Schema/Schema";
import { getProjectRoles } from "../../API/Role/Role";
import { getPortals } from "../../API/Portal/Portal";
import { getAttributes } from '../../API/Attribute/Attribute';
import { requestInformationFromAppModel } from './utils';
import ChatContextDropdown from './ChatContextDropdown';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import { sendMessage, addContextMessageToAssistant, getCurrentUserId } from './chatboxApi';
import { handleExpandChange, handleSubTreeExpandChange } from './chatboxHandlers';
import { validateApp, performIncrementalGeneration } from './chatboxPublish';

const user = {
    id: 1
};

const bot = {
    id: 0
};

function AIChatBox({ context, entities, entityId, pageSysName, instanceId, projectId, subscriptionId, organizationId }) {
    const [threadId, setThreadId] = useState(null);
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState([]);
    const [treeOptions, setTreeOptions] = useState([
        {
            id: 1,
            name: "Entities",
            isExpanded: false
        },
        { 
            id: 2,
            name: "Entity Relations",
            isExpanded: false
        },
        {
            id: 3,
            name: "Roles",
            isExpanded: false
        },
        {
            id: 4,
            name: "Portals",
            isExpanded: false
        }
    ]);
    const [treeValue, setTreeValue] = useState(null);
    const [subTreeOptions, setSubTreeOptions] = useState([]);
    const [subTreeValue, setSubTreeValue] = useState(null);
    const [allEntities, setAllEntities] = useState(entities);
    const [loading, setLoading] = useState(false);
    const contextLoading = useRef(false);
    const msalContext = useMsal();

    const nextContext = useRef(null);
    const [lastMessageId, setLastMessageId] = useState(null);

    useEffect(() => {
        async function fetchThreadId() {
            const request = await callApiWithAuthorization('/api/v0/test/thread', {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ context })
            });
            const response = await request.json();
            setThreadId(response.threadId);
        }
        fetchThreadId();
        // document.addEventListener("open", getContextFromDesignerAppModel)

        // return () => {
        //     document.removeEventListener("open", getContextFromDesignerAppModel)
        // }
    }, [])

    async function initContext() {
        contextLoading.current = true;
        if ((entityId || pageSysName) && pageSysName !== "_settings") {
            const entity = allEntities.find(entity => entity._id === entityId);
            if (entity) {
                if (pageSysName) {
                    const pages = await getEntityPages(entity._id);
                    const page = pages.data.find(page => page.sysPageName === pageSysName);
                    if (page) {
                        setTreeValue({
                            id: entity._id,
                            name: entity.name
                        })
                        setSubTreeValue({
                            id: page._id,
                            name: page.pageName
                        });
                        setLoading(true);
                        setMessages((prev) => [...prev, {
                            role: "system",
                            content: "Loading new context..."
                        }]);
                        const message = `I have navigated to the page "${page.pageName}" (id: ${page._id}) of the entity "${entity.name}" (id: ${entity._id}). Acknowledge this and send OK.`;
                        const response = await sendMessage({
                            threadId,
                            prompt: message,
                            projectId,
                            subscriptionId,
                            organizationId,
                            previousResponseId: lastMessageId
                        });
                        setLastMessageId(response.id);
                        setMessages((prev) => [
                            ...prev.filter(msg => msg.content !== "Loading new context..."),
                            {
                                role: "info",
                                content: entity.name + "'s " + page.pageName + " is now the current page."
                            }
                        ]);
                        setLoading(false);
                    } else {
                        setTreeValue({
                            id: entity._id,
                            name: entity.name
                        })
                        setSubTreeValue(null);
                        setLoading(true);
                        setMessages((prev) => [...prev, {
                            role: "system",
                            content: "Loading new context..."
                        }]);
                        const message = `I have navigated to the entity "${entity.name}" (id: ${entity._id}), but the selected page was not found. Acknowledge this and send OK.`;
                        const response = await sendMessage({
                            threadId,
                            prompt: message,
                            projectId,
                            subscriptionId,
                            organizationId,
                            previousResponseId: lastMessageId
                        });
                        setLastMessageId(response.id);
                        setMessages((prev) => [
                            ...prev.filter(msg => msg.content !== "Loading new context..."),
                            {
                                role: "info",
                                content: entity.name + " is now the current entity. The page selected cannot be found."
                            }
                        ]);
                        setLoading(false);
                    }
                } else {
                    setTreeValue({
                        id: entity._id,
                        name: entity.name
                    })
                    setSubTreeValue(null);
                    setLoading(true);
                    setMessages((prev) => [...prev, {
                        role: "system",
                        content: "Loading new context..."
                    }]);
                    const message = `I have navigated to the entity "${entity.name}" (id: ${entity._id}). Acknowledge this and send OK.`;
                    const response = await sendMessage({
                        threadId,
                        prompt: message,
                        projectId,
                        subscriptionId,
                        organizationId,
                        previousResponseId: lastMessageId
                    });
                    setLastMessageId(response.id);
                    setMessages((prev) => [
                        ...prev.filter(msg => msg.content !== "Loading new context..."),
                        {
                            role: "info",
                            content: entity.name + " is now the current entity."
                        }
                    ]);
                    setLoading(false);
                }
            }
        } else {
            requestInformationFromAppModel(organizationId, projectId, instanceId);
        }
        contextLoading.current = false;
    }

    useEffect(() => {
        if (threadId) {
            initContext();
        }
        window.addEventListener("message", handleMessage);

        return () => {
            window.removeEventListener("message", handleMessage);
        }
    }, [threadId]);

    useEffect(() => {
        getTreeOptionsData();
    }, [projectId]);

    useEffect(() => {
        // scroll to bottom of chat
        const chatBody = document.querySelector('.chat-body');
        if (chatBody) {
            chatBody.scrollTop = chatBody.scrollHeight;
        }
    }, [messages])

    async function getTreeOptionsData() {
        const entities = await getEntities(projectId);
        const entityRelations = await getProjectRelations(projectId);
        const roles = await getProjectRoles(projectId);
        const portals = await getPortals(projectId);

        const treeOptions = [
            {
                id: 1,
                name: "Entities",
                isExpanded: false,
                children: entities.map(entity => ({
                    id: entity._id,
                    name: entity.name,
                    type: "entity"
                }))
            },
            {
                id: 2,
                name: "Entity Relations",
                isExpanded: false,
                children: entityRelations.map(relation => ({
                    id: relation._id,
                    name: relation.identifier,
                    type: "relation"
                }))
            },
            {
                id: 3,
                name: "Roles",
                isExpanded: false,
                children: roles.map(role => ({
                    id: role._id,
                    name: role.roleName,
                    type: "role"
                }))
            },
            {
                id: 4,
                name: "Portals",
                isExpanded: false,
                children: portals.map(portal => ({
                    id: portal._id,
                    name: portal.portalName,
                    type: "portal"
                }))
            }
        ]
        setTreeOptions(treeOptions);
    }

    // Replace inline handler functions with imported ones where possible
    // Example for expand/collapse handlers:
    const onExpandChange = handleExpandChange(setTreeOptions);
    const onSubTreeExpandChange = handleSubTreeExpandChange(setSubTreeOptions);

    async function handleChange(event, subTreeValue) {
        setLoading(true);
        setMessages((prev) => [...prev, {
            role: "system",
            content: "Loading new context..."
        }]);
        let topLevelRoot = treeOptions.find(option => option.id === event.value.id);
        let children = null;

        if (topLevelRoot === undefined) {
            for (const option of treeOptions) {
                const childrenIndex = option.children.findIndex(child => child.id === event.value.id);
                if (childrenIndex > -1) {
                    topLevelRoot = option;
                    children = option.children[childrenIndex];
                    break;
                }
            }
            let message = "";
            if (topLevelRoot && children) {
                message = `I have selected the ${topLevelRoot.name.slice(0, -1).toLowerCase()} "${children.name}" with id ${children.id}.`;
            }
            if (message) {
                const response = await sendMessage({
                    threadId,
                    prompt: message,
                    projectId,
                    subscriptionId,
                    organizationId,
                    previousResponseId: lastMessageId
                });
                setLastMessageId(response.id);
                setMessages((prev) => [...prev, {
                    role: "info",
                    content: `${children.name} ${topLevelRoot.name} is now selected.`
                }]);
            }
        } else {
            let message = `I have selected the ${topLevelRoot.name.toLowerCase()} root.`;
            const response = await sendMessage({
                threadId,
                prompt: message,
                projectId,
                subscriptionId,
                organizationId,
                previousResponseId: lastMessageId
            });
            setLastMessageId(response.id);
            setMessages((prev) => [...prev, {
                role: "info",
                content: topLevelRoot.name + " is now selected."
            }]);
        }
        if (topLevelRoot.name === "Entities" && children !== null) {
            getAttributes(children.id).then(async attributes => {
                const pages = await getEntityPages(children.id);
                setSubTreeOptions([
                    {
                        id: 1,
                        name: "Attributes",
                        isExpanded: false,
                        children: attributes.map(attribute => ({
                            id: attribute._id,
                            name: attribute.attributeName
                        }))
                    },
                    {
                        id: 2,
                        name: "Pages",
                        isExpanded: false,
                        children: pages.data.map(page => ({
                            id: page._id,
                            name: page.pageName
                        }))
                    }
                ])
                if (subTreeValue && subTreeValue.id) {
                    const attribute = attributes.find(attribute => attribute._id === subTreeValue.id);
                    if (attribute) {
                        setSubTreeValue({
                            id: attribute._id,
                            name: attribute.attributeName
                        });
                    } else {
                        const page = pages.data.find(page => page._id === subTreeValue.id);
                        if (page) {
                            setSubTreeValue({
                                id: page._id,
                                name: page.pageName
                            });
                        }
                    }
                }
            });
        } else {
            setSubTreeOptions([
                {
                    id: 1,
                    name: "Entities",
                    isExpanded: true,
                    children: allEntities.map(entity => ({
                        id: entity._id,
                        name: entity.name
                    }))
                }
            ]);
        }

        setTreeValue(event.value);
        setMessages((prev) => prev.filter(msg => msg.content !== "Loading new context..."));
        setLoading(false);
    }

    async function handleSubTreeChange(event) {
        setLoading(true);
        setMessages((prev) => [...prev, {
            role: "system",
            content: "Loading new context..."
        }]);
        setSubTreeValue(event.value);

        let treeTopLevelRoot = treeOptions.find(option => option.id === treeValue?.id);
        let treeChildren = null;
        if (treeTopLevelRoot === undefined && treeValue) {
            for (const option of treeOptions) {
                const childrenIndex = option.children.findIndex(child => child.id === treeValue.id);
                if (childrenIndex > -1) {
                    treeTopLevelRoot = option;
                    treeChildren = option.children[childrenIndex];
                    break;
                }
            }
        }

        let topLevelRoot = subTreeOptions.find(option => option.id === event.value.id);
        let children = null;
        if (topLevelRoot === undefined) {
            for (const option of subTreeOptions) {
                const childrenIndex = option.children.findIndex(child => child.id === event.value.id);
                if (childrenIndex > -1) {
                    topLevelRoot = option;
                    children = option.children[childrenIndex];
                    break;
                }
            }
        }

        let message = "";
        if (treeTopLevelRoot && topLevelRoot && children) {
            message = `I have selected the ${topLevelRoot.name.slice(0, -1).toLowerCase()} "${children.name}" with id ${children.id} inside ${treeTopLevelRoot.name}.`;
        } else if (treeTopLevelRoot && topLevelRoot) {
            message = `I have selected the ${topLevelRoot.name.toLowerCase()} root inside ${treeTopLevelRoot.name}.`;
        }
        if (message) {
            const response = await sendMessage({
                threadId,
                prompt: message,
                projectId,
                subscriptionId,
                organizationId,
                previousResponseId: lastMessageId
            });
            setLastMessageId(response.id);
            setMessages((prev) => [...prev, {
                role: "info",
                content: (children ? children.name : topLevelRoot?.name) + " is now selected."
            }]);
        }
        setMessages((prev) => prev.filter(msg => msg.content !== "Loading new context..."));
        setLoading(false);
    }

    async function addContextMessageToAssistant(context) {
        debugger;
        const request = await callApiWithAuthorization('/api/v0/ai/add-message-to-thread', {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                threadId,
                message: context,
                role: "user"
            })
        });
        const response = await request.json();
        return response;
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (loading) return;
        const msg = input.trim();
        setMessages(messages => ([...messages, { sender: user.id, text: input }]));
        setInput('');
        setLoading(true);
        setMessages(messages => ([...messages, { role: "system", content: "AI is thinking..." }]));
        const response = await sendMessage({
            threadId,
            prompt: msg,
            projectId,
            subscriptionId,
            organizationId,
            previousResponseId: lastMessageId
        });
        setLastMessageId(response.id);
        setMessages(messages => ([
            ...messages.filter(m => m.content !== "AI is thinking..."),
            {
                sender: bot.id,
                text: response.message,
                toolCallsMadeAndResponses: response.toolCallsMadeAndResponses,
                showToolCalls: false
            }
        ]));
        setLoading(false);
    }

    // async function sendMessage(prompt) {
    //     const request = await callApiWithAuthorization('/api/v0/prompt-ai', {
    //         method: "POST",
    //         headers: {
    //             "Content-Type": "application/json"
    //         },
    //         body: JSON.stringify({
    //             threadId: threadId,
    //             prompt,
    //             projectId,
    //             subscriptionId,
    //             organizationId
    //         })
    //     });
    //     const response = await request.json();
    //     return response;
    // }

    function toggleShowToolCalls(index) {
        setMessages(messages => {
            const newMessages = [...messages];
            newMessages[index].showToolCalls = !newMessages[index].showToolCalls;
            return newMessages;
        });
    }

    async function handleMessage(event) {
        try {
            if (event.origin === window.location.origin) {
                const json = typeof event.data === "string" ? JSON.parse(event.data) : undefined;
                if (json) {
                    if (contextLoading.current) {
                        nextContext.current = {
                            function: 'handleMessage',
                            data: json.data
                        };
                        return;
                    } else {
                        contextLoading.current = true;
                    }
                    switch (json.type) {
                        case "entitySelected": {
                            const entity = json.data;
                            handleChange({
                                value: {
                                    id: entity._id,
                                    name: entity.name
                                }
                            })
                            setSubTreeValue(null);
                            break;
                        }
                        case "entityAttributeSelected": {
                            const attribute = json.data;
                            handleSubTreeChange({
                                value: {
                                    id: attribute._id,
                                    name: attribute.attributeName
                                }
                            })
                            break;
                        }
                        case "entityRelationSelected": {
                            const relation = json.data;
                            handleChange({
                                value: {
                                    id: relation.relationId,
                                    name: relation.relationIdentifier
                                }
                            })
                            setSubTreeValue(null);
                            break;
                        }
                        case "roleSelected": {
                            const role = json.data;
                            handleChange({
                                value: {
                                    id: role._id,
                                    name: role.roleName
                                }
                            })
                            setSubTreeValue(null);
                            break;
                        }
                        case "portalSelected": {
                            const portal = json.data;
                            handleChange({
                                value: {
                                    id: portal._id,
                                    name: portal.portalName
                                }
                            })
                            setSubTreeValue(null);
                            break;
                        }
                        case "changeContext": {
                            changeContext(json.data)
                            break;
                        }
                        default:
                            break;
                    }
                    contextLoading.current = false;
                }
            } else if (event.origin === process.env.REACT_APP_RUNTIME_URL) {
                const data = JSON.parse(event.data);
                if (typeof event.data === "string") {
                    if (contextLoading.current) {
                        nextContext.current = {
                            function: 'RuntimePageChange',
                            entitySysName: data.queryParams.entitySysName,
                            pageSysName: data.queryParams.pageSysName
                        };
                        return;
                    } else {
                        contextLoading.current = true;
                    }
                    if (data.message === "iframeParams") {
                        await handleRuntimePageChange(data.queryParams.entitySysName, data.queryParams.pageSysName);
                    }
                }
            }
            contextLoading.current = false;
        } catch (e) {
            setMessages((prev) => prev.filter(msg => msg.content !== "Loading new context..."));
            setLoading(false);
            contextLoading.current = false;
            console.error(e);
        }
    }

    async function handleRuntimePageChange(entitySysName, pageSysName) {
        setLoading(true);
        setMessages((prev) => [...prev, {
            role: "system",
            content: "Loading new context..."
        }]);
        const entities = await getEntities(projectId);
        if (entities && entities.length > 0) {
            const entity = entities.find(entity => entity.sysName === entitySysName);
            if (entity) {
                const pages = await getEntityPages(entity._id);
                if (pages && pages.data && pages.data.length > 0) {
                    const page = pages.data.find(page => page.sysPageName === pageSysName);
                    if (page && page._id) {
                        let message = "";
                        if (page.subPageType === "Single Record") {
                            message = `I have navigated to the single record page "${page.pageName}" (id: ${page._id}) for the entity "${entity.name}" (id: ${entity._id}). Working on single record pages using prompts is still in development. Acknowledge this and send OK.`;
                        } else {
                            message = `I have navigated to the page "${page.pageName}" (id: ${page._id}) of the entity "${entity.name}" (id: ${entity._id}). Acknowledge this and send OK.`;
                        }
                        const attributes = getAttributes(entity._id);
                        const pages = getEntityPages(entity._id);
                        const response = await sendMessage({
                            threadId,
                            prompt: message,
                            projectId,
                            subscriptionId,
                            organizationId,
                            previousResponseId: lastMessageId
                        });
                        setLastMessageId(response.id);
                        setMessages((prev) => [...prev, {
                            role: "info",
                            content: entity.name + "'s " + page.pageName + " is now the current page."
                        }]);
                        setTreeValue({
                            id: entity._id,
                            name: entity.name
                        })
                        setSubTreeValue({
                            id: page._id,
                            name: page.pageName
                        });
                    } else {
                        const message = `I have navigated to the entity "${entity.name}" (id: ${entity._id}), but the selected page was not found. Acknowledge this and send OK.`;
                        const response = await sendMessage({
                            threadId,
                            prompt: message,
                            projectId,
                            subscriptionId,
                            organizationId,
                            previousResponseId: lastMessageId
                        });
                        setLastMessageId(response.id);
                        setMessages((prev) => [...prev, {
                            role: "info",
                            content: entity.name + " is now the current entity. The page selected cannot be found."
                        }]);
                        setTreeValue({
                            id: entity._id,
                            name: entity.name
                        })
                        setSubTreeValue(null);
                    }
                } else {
                    const message = `I have navigated to the entity "${entity.name}" (id: ${entity._id}), but this entity does not have any pages. Acknowledge this and send OK.`;
                    const response = await sendMessage({
                        threadId,
                        prompt: message,
                        projectId,
                        subscriptionId,
                        organizationId,
                        previousResponseId: lastMessageId
                    });
                    setLastMessageId(response.id);
                    setMessages((prev) => [...prev, {
                        role: "info",
                        content: entity.name + " is now the current entity. The entity does not have any pages."
                    }]);
                    setTreeValue({
                        id: entity._id,
                        name: entity.name
                    })
                    setSubTreeValue(null);
                }
            } else {
                const message = `I have navigated to an entity which does not exist (sysName: ${entitySysName}). Acknowledge this and send OK.`;
                const response = await sendMessage({
                    threadId,
                    prompt: message,
                    projectId,
                    subscriptionId,
                    organizationId,
                    previousResponseId: lastMessageId
                });
                setLastMessageId(response.id);
                setMessages((prev) => [...prev, {
                    role: "info",
                    content: "The entity selected cannot be found."
                }]);
                setTreeValue(null);
                setSubTreeValue(null);
            }
        } else {
            const message = `I have navigated to an entity, but no entities were found in the project. Acknowledge this and send OK.`;
            const response = await sendMessage({
                threadId,
                prompt: message,
                projectId,
                subscriptionId,
                organizationId,
                previousResponseId: lastMessageId
            });
            setLastMessageId(response.id);
            setMessages((prev) => [...prev, {
                role: "info",
                content: "No entities found in the project."
            }]);
            setTreeValue(null);
            setSubTreeValue(null);
        }
        setMessages((prev) => prev.filter(msg => msg.content !== "Loading new context..."));
        setLoading(false);
        contextLoading.current = false;
    }

    async function changeContext(properties) {
        const { tab, entityId, attributeId, entityRelationId, roleId, portalId, pageId } = properties;
        if (attributeId || pageId) {
            if (entityId) {
                const entity = allEntities.find(entity => entity._id === entityId);
                if (!entity) {
                    throw new Error("Entity with id " + entityId + " does not exist.");
                }
            } else {
                throw new Error("Entity id is required.");
            }
        }
        let message = "";
        if (entityId) {
            if (attributeId) {
                const entity = allEntities.find(entity => entity._id === entityId);
                const attributes = await getAttributes(entityId);
                const attribute = attributes.find(attribute => attribute._id === attributeId);
                if (!attribute) {
                    throw new Error("Attribute with id " + attributeId + " does not exist.");
                }
                handleChange({
                    value: {
                        id: entity._id,
                        name: entity.name
                    }
                })
                setSubTreeValue({
                    id: attribute._id,
                    name: attribute.attributeName
                })
                // handleSubTreeChange({
                //     value: {
                //         id: attribute._id,
                //         name: attribute.attributeName
                //     }
                // })
                message = `
                    Now the user is at the entity ${entity.name} and the current attribute selected by the user is ${attribute.attributeName}
                    The details of the selected entity is: ${JSON.stringify(entity)}
                    The details of the selected attribute is: ${JSON.stringify(attribute)}
                `
            } else if (pageId) {
                const entity = allEntities.find(entity => entity._id === entityId);
                const pages = await getEntityPages(entityId);
                const page = pages.data.find(page => page._id === pageId);
                if (!page) {
                    throw new Error("Page with id " + pageId + " does not exist.");
                }
                handleChange({
                    value: {
                        id: entity._id,
                        name: entity.name
                    }
                })
                handleSubTreeChange({
                    value: {
                        id: page._id,
                        name: page.pageName
                    }
                })
                const { component, ...pageProperties } = page;
                message = `
                    Now the user is at the entity ${entity.name} and the current page selected by the user is ${page.pageName}
                    The details of the selected entity is: ${JSON.stringify(entity)}
                    The details of the selected page is: ${JSON.stringify(pageProperties)}
                `;
            } else if (!attributeId && !pageId) {
                const entities = await getEntities(projectId);
                const entity = entities.find(entity => entity._id === entityId);
                handleChange({
                    value: {
                        id: entity._id,
                        name: entity.name
                    }
                })
                setSubTreeValue(null);
                message = `
                    Now the user is at the entity ${entity.name}
                    The details of the selected entity is: ${JSON.stringify(entity)}
                `;
            }
        } else if (entityRelationId) {
            const relations = await getProjectRelations(projectId);
            const relation = relations.find(relation => relation._id === entityRelationId);
            if (!relation) {
                throw new Error("Relation with id " + entityRelationId + " does not exist.");
            }
            handleChange({
                value: {
                    id: relation._id,
                    name: relation.identifier
                }
            })
            setSubTreeValue(null);
            message = `
                Now the user is at the relation ${relation.identifier}
                The details of the selected relation is: ${JSON.stringify(relation)}
            `;
        } else if (roleId) {
            const roles = await getProjectRoles(projectId);
            const role = roles.find(role => role._id === roleId);
            if (!role) {
                throw new Error("Role with id " + roleId + " does not exist.");
            }
            handleChange({
                value: {
                    id: role._id,
                    name: role.roleName
                }
            })
            setSubTreeValue(null);
            message = `
                Now the user is at the role ${role.roleName}
                The details of the selected role is: ${JSON.stringify(role)}
            `;
        } else if (portalId) {
            const portals = await getPortals(projectId);
            const portal = portals.find(portal => portal._id === portalId);
            if (!portal) {
                throw new Error("Portal with id " + portalId + " does not exist.");
            }
            handleChange({
                value: {
                    id: portal._id,
                    name: portal.portalName
                }
            })
            setSubTreeValue(null);
            message = `
                Now the user is at the portal ${portal.portalName}
                The details of the selected portal is: ${JSON.stringify(portal)}
            `;
        } else if (!entityId && !roleId && !portalId) {
            const tab = treeOptions.find(option => option.name === properties.tab);
            setTreeValue(tab);
            setSubTreeValue(null);
            message = `Now the user is at the tab ${tab.name}`;
        }
        contextLoading.current = false;
        return message.trim()
    }

    async function validateApp() {
        const response = await validateForPublish(projectId, "production");
        if (response.error) {
            throw new Error(response.error);
        }
    }

    async function getCurrentUserId() {
        if (msalContext.accounts.length === 0) {
            const user = await getLoggedInUserUsingAccessToken();
            return user.azureId;
        }
        if (msalContext.accounts.length > 0)
            return msalContext.accounts[0].localAccountId;
        throw new Error("User is not logged in to designer");
    }

    async function performIncrementalGeneration() {
        const { instances } = await getProject(projectId);
        const productionInstance = instances.find(instance => instance.instanceType === "Production");
        try {
            await validateApp();
            const userId = await getCurrentUserId();
            const [compressedJson, generationError] = await generateIncrementalChanges(
                projectId, organizationId, userId, productionInstance._id, false, false
            );
            if (generationError) {
                throw new Error(generationError);
            }
            await removeAllChanges(projectId);
            updateProjectPortalConfig(projectId);
            const result = await publishApplication(msalContext, productionInstance, compressedJson, false);
            if (result.status !== "Success") {
                throw new Error(result.message);
            }
            await updateProjectPropertiesAfterPublish(projectId, productionInstance._id);
            postMessageToRuntime({
                designerStatus: "Success"
            })
        } catch (e) {
            throw new Error(e.message + ". Do not attempt to republish the application again. Just show the error message to the user.");
        }
    }

    async function handlePublishFromButton() {
        setLoading(true);
        try {
            await performIncrementalGeneration();
            setMessages((prev) => [...prev, {
                role: "system",
                content: "Application published successfully."
            }]);
        } catch (e) {
            setMessages((prev) => [...prev, {
                role: "system",
                content: "Publish failed: " + e.message
            }]);
            console.error(e);
        }
        setLoading(false);
    }

    useEffect(() => {
        if (!contextLoading.current && nextContext.current) {
            const next = nextContext.current;
            nextContext.current = null;
            contextLoading.current = true;
            (async () => {
                try {
                    if (next.function === 'handleMessage') {
                        debugger;
                        await handleMessage({ data: JSON.stringify({ data: next.data }), origin: window.location.origin });
                    } else if (next.function === 'RuntimePageChange') {
                        await handleRuntimePageChange(next.entitySysName, next.pageSysName);
                    }
                } finally {
                    contextLoading.current = false;
                }
            })();
        }
    }, [messages]);

    return (
        <div className='flex-grow-1 d-flex flex-column'>
            <div className='flex-grow-1 position-relative'>
                <div className='position-absolute top-0 start-0 w-100 h-100 overflow-auto'>
                    <div className='w-100 d-flex justify-content-between align-items-end pb-2 border-bottom'>
                        <ChatContextDropdown
                            treeOptions={treeOptions}
                            treeValue={treeValue}
                            subTreeOptions={subTreeOptions}
                            subTreeValue={subTreeValue}
                            loading={loading}
                            handleExpandChange={onExpandChange}
                            handleChange={handleChange}
                            handleSubTreeExpandChange={onSubTreeExpandChange}
                            handleSubTreeChange={handleSubTreeChange}
                        />
                        <div>
                            <button className="btn btn-primary" onClick={handlePublishFromButton} disabled={loading}>
                                Publish
                            </button>
                        </div>
                    </div>
                    <ChatMessages
                        messages={messages}
                        user={user}
                        bot={bot}
                        loading={loading}
                        toggleShowToolCalls={toggleShowToolCalls}
                    />
                </div>
            </div>
            <div className='flex-shrink-0'>
                <ChatInput
                    input={input}
                    setInput={setInput}
                    loading={loading}
                    handleSubmit={handleSubmit}
                />
            </div>
            <style>
                {`
                .chat-info-message {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    color: #1976d2;
                    background: #e3f2fd;
                    border-radius: 12px;
                    margin: 5px 0;
                    padding: 14px 24px;
                    font-weight: 500;
                    font-size: 1.05rem;
                    max-width: 80%;
                    margin-left: auto;
                    margin-right: auto;
                    text-align: center;
                }
                `}
            </style>
        </div>
    );
}

export default AIChatBox;